<div class="panel__header">
  <div>
    <h2>{{ 'admin.support.title' | adminTranslate }}</h2>
  </div>
  <div class="panel__actions">
    <div class="toolbar toolbar--compact">
      <ui-select size="sm" [(ngModel)]="statusFilter" (ngModelChange)="loadSupportSessions()">
        <option value="">{{ 'admin.common.allStatuses' | adminTranslate }}</option>
        <option value="ACTIVE">{{ 'admin.common.active' | adminTranslate }}</option>
        <option value="CLOSED">{{ 'admin.common.closed' | adminTranslate }}</option>
        <option value="EXPIRED">{{ 'admin.common.expired' | adminTranslate }}</option>
      </ui-select>
    </div>
    <button
      type="button"
      class="btn-icon btn-icon-refresh"
      (click)="loadSupportSessions()"
      [disabled]="loadingSessions"
      [title]="'admin.common.refresh' | adminTranslate"
      [attr.aria-label]="'admin.common.refresh' | adminTranslate">
      <ui-icon name="refresh"></ui-icon>
    </button>
  </div>
</div>

@if (loadingSessions) {
  <p class="empty">{{ 'admin.support.loading' | adminTranslate }}</p>
} @else if (sessions.length) {
  <div class="support-table-wrap">
    <table class="support-table">
      <thead>
        <tr>
          <th>{{ 'admin.support.session' | adminTranslate }}</th>
          <th>{{ 'admin.support.dates' | adminTranslate }}</th>
          <th>{{ 'admin.support.permissions' | adminTranslate }}</th>
          <th>{{ 'admin.support.actions' | adminTranslate }}</th>
        </tr>
      </thead>
      <tbody>
        @for (session of sessions; track session.id) {
          <tr>
            <td>
              <div class="session-card">
                <div class="session-badge">
                  <span class="session-person">
                    @if (session.actorProfileImageUrl) {
                      <img [src]="session.actorProfileImageUrl" alt="" class="session-avatar" width="30" height="30" />
                    } @else {
                      <span class="session-avatar session-avatar--fallback">{{ avatarInitial(session.actorUsername || session.actorUserId) }}</span>
                    }
                    <span class="session-person__name">{{ session.actorUsername || session.actorUserId }}</span>
                  </span>
                  <span class="session-arrow">&gt;</span>
                  <a class="session-person session-person--target" [routerLink]="['/users', session.targetUserId]">
                    @if (session.targetProfileImageUrl) {
                      <img [src]="session.targetProfileImageUrl" alt="" class="session-avatar" width="30" height="30" />
                    } @else {
                      <span class="session-avatar session-avatar--fallback">{{ avatarInitial(session.targetUsername || session.targetUserId) }}</span>
                    }
                    <span class="session-person__name">{{ session.targetUsername || session.targetUserId }}</span>
                  </a>
                  <span class="status" [class.status--active]="session.status === 'ACTIVE'" [class.status--closed]="session.status === 'CLOSED'" [class.status--expired]="session.status === 'EXPIRED'">
                    {{ statusLabel(session.status) }}
                  </span>
                </div>
                <p class="session-reason">{{ session.reason || ('admin.support.noReason' | adminTranslate) }}</p>
              </div>
            </td>
            <td>
              <div class="table-dates">
                <span>{{ formatSupportDate('created', session.createdAt) }}</span>
                <span>{{ formatSupportDate('expires', session.expiresAt) }}</span>
                <span>{{ formatSupportDate('lastExchange', session.lastExchangedAt) }}</span>
              </div>
            </td>
            <td>
              <div class="chips">
                @for (group of groupedPermissions(session.permissions); track group.key) {
                  <span class="permission-badge" [class.permission-badge--accent]="group.variant === 'accent'" [class.permission-badge--warning]="group.variant === 'warning'" [class.permission-badge--info]="group.variant === 'info'" [class.permission-badge--success]="group.variant === 'success'" [class.permission-badge--neutral]="group.variant === 'neutral'">
                    <span class="permission-badge__label">{{ group.labelKey | adminTranslate }} :</span>
                    <span class="permission-badge__rights">
                      @for (right of group.rights; track right) {
                        <span class="permission-right" [class.permission-right--read]="right === 'read'" [class.permission-right--write]="right === 'write'" [attr.title]="('admin.common.rights.' + right) | adminTranslate" [attr.aria-label]="('admin.common.rights.' + right) | adminTranslate">
                          @if (right === 'read') {
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6S2 12 2 12Z"></path>
                              <circle cx="12" cy="12" r="3.2"></circle>
                            </svg>
                          } @else {
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="m3 17.25 9.9-9.9 3.75 3.75-9.9 9.9L3 21z"></path>
                              <path d="M14.1 6.15 16.2 4a1.8 1.8 0 0 1 2.55 0l1.25 1.25a1.8 1.8 0 0 1 0 2.55l-2.15 2.1"></path>
                            </svg>
                          }
                        </span>
                      }
                    </span>
                  </span>
                }
              </div>
            </td>
            <td>
              <div class="table-actions">
                <div class="table-actions__buttons">
                  @if (session.status === 'ACTIVE') {
                    <ui-button
                      variant="primary"
                      size="sm"
                      (click)="resumeSession(session)"
                      [disabled]="resumingSessionId === session.id || !canResume(session)"
                      [title]="sessionOwnerHint(session) || ''">
                      {{ resumingSessionId === session.id ? ('admin.support.resuming' | adminTranslate) : ('admin.support.resume' | adminTranslate) }}
                    </ui-button>
                  }
                  <ui-button variant="secondary" size="sm" (click)="openAudit(session)">{{ 'admin.support.viewAudit' | adminTranslate }}</ui-button>
                </div>
                @if (sessionOwnerHint(session); as hint) {
                  <p class="session-reason">{{ hint }}</p>
                }
                @if (session.status === 'ACTIVE') {
                  <div class="table-actions__close">
                    <ui-input [(ngModel)]="closeReasons[session.id]" size="sm" [placeholder]="'admin.support.closeReasonPlaceholder' | adminTranslate"></ui-input>
                    <ui-button variant="danger" size="sm" (click)="closeSession(session)" [disabled]="closingSessionId === session.id">
                      {{ closingSessionId === session.id ? ('admin.common.closing' | adminTranslate) : ('admin.common.close' | adminTranslate) }}
                    </ui-button>
                  </div>
                }
              </div>
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>
} @else {
  <p class="empty">{{ 'admin.support.empty' | adminTranslate }}</p>
}
